import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import { RootState, ThunkServices } from '../../app/store';
import { push } from 'connected-react-router';
import { appRoutesByName } from '../../containers/app/Routes';
import moment from 'moment-timezone';

// State
export interface AvailabilityState {
    availableTimes: {
        [key: string]: endpoints.LessonTime[];
    };
    firstAvailableDay?: Date;
    daysSearched?: Date[];
    availableTimesForDay?: endpoints.LessonTime[];
    availableDay?: Date;
    loading: boolean;
    searchingDay: boolean;
}

export const initialState: AvailabilityState = {
    availableTimes: {},
    firstAvailableDay: undefined,
    loading: false,
    searchingDay: false,
};

// Thunks
export const getFirstAvailability = createAsyncThunk<
    endpoints.LessonTime[], // Return type of the thunk
    number, // Argument type (geoareaId)
    { state: RootState; extra: ThunkServices } // ThunkAPI configuration
>('availability/getFirstAvailability', async (geoareaId, { dispatch, extra: { availabilityService } }) => {
    dispatch(setAvailabilityLoading(true));
    try {
        const response = await availabilityService.getFirstAvailability(geoareaId);
        const firstAvailableDay = new Date(Object.keys(response)[0]);
        firstAvailableDay.setHours(1, 0, 0, 0);
        dispatch(setFirstAvailableDay({ firstAvailableDay }));
        dispatch(setAvailability({ availableTimesForDay: response }));
        return response;
    } catch (error) {
        dispatch(setAvailabilityLoading(false));
        dispatch<any>(push(appRoutesByName.errorPage.path));
        throw error;
    }
});

// Action to reset availableTimes
export const resetAvailability = () => (dispatch: any) => {
    dispatch(availabilitySlice.actions._resetAvailability());
};

export const getAvailabilityForDay = createAsyncThunk<
    endpoints.LessonTime[], // Return type of the thunk
    { geoareaId: number; date: Date }, // Argument type
    { state: RootState; extra: ThunkServices } // ThunkAPI configuration
>('availability/getAvailabilityForDay', async ({ geoareaId, date }, { dispatch, extra: { availabilityService } }) => {
    dispatch(setSearching(true));
    try {
        const response = await availabilityService.getAvailabilityForDay(geoareaId, date);
        const availableDay = new Date(date);
        availableDay.setHours(1, 0, 0, 0);
        dispatch(addSearchedDay({ availableDay }));
        dispatch(addAvailability({ availableTimesForDay: response }));
        return response;
    } catch (error) {
        const availableDay = new Date(date);
        availableDay.setHours(1, 0, 0, 0);
        dispatch(addSearchedDay({ availableDay }));
        dispatch(setSearching(false));
        dispatch<any>(push(appRoutesByName.errorPage.path));
        throw error;
    }
});

// Slice
export const availabilitySlice = createSlice({
    name: 'availability',
    initialState,
    reducers: {
        _resetAvailability: (state) => {
            state.availableTimes = {};
            state.firstAvailableDay = undefined;
            state.daysSearched = undefined;
            state.availableTimesForDay = undefined;
            state.availableDay = undefined;
            state.loading = false;
            state.searchingDay = false;
        },
        setAvailability: (state, action: PayloadAction<{ availableTimesForDay: endpoints.LessonTime[] }>) => {
            if (!isEmpty(action.payload.availableTimesForDay)) {
                const currentLessonSetDateTimeString = Object.keys(action.payload.availableTimesForDay)[0];
                state.availableTimes[moment(currentLessonSetDateTimeString).format('DD-MM-YYYY')] =
                    action.payload.availableTimesForDay;
            }
        },
        addAvailability: (state, action: PayloadAction<{ availableTimesForDay: endpoints.LessonTime[] }>) => {
            if (!isEmpty(action.payload.availableTimesForDay)) {
                const currentLessonSetDateTimeString = Object.keys(action.payload.availableTimesForDay)[0];
                state.availableTimes = {
                    ...state.availableTimes,
                    [moment(currentLessonSetDateTimeString).format('DD-MM-YYYY')]: action.payload.availableTimesForDay,
                };
            }
        },
        setFirstAvailableDay: (state, action: PayloadAction<{ firstAvailableDay: Date }>) => {
            state.firstAvailableDay = action.payload.firstAvailableDay;
            state.daysSearched = [action.payload.firstAvailableDay];
        },
        addSearchedDay: (state, action: PayloadAction<{ availableDay: Date }>) => {
            state.daysSearched = state.daysSearched
                ? [...state.daysSearched, action.payload.availableDay]
                : [action.payload.availableDay];
        },
        setAvailabilityLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setSearching: (state, action: PayloadAction<boolean>) => {
            state.searchingDay = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getFirstAvailability.pending, (state) => {
                state.loading = true;
            })
            .addCase(getFirstAvailability.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(getFirstAvailability.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getAvailabilityForDay.pending, (state) => {
                state.searchingDay = true;
            })
            .addCase(getAvailabilityForDay.fulfilled, (state) => {
                state.searchingDay = false;
            })
            .addCase(getAvailabilityForDay.rejected, (state) => {
                state.searchingDay = false;
            });
    },
});

// Actions
export const {
    setAvailability,
    addAvailability,
    setFirstAvailableDay,
    addSearchedDay,
    setAvailabilityLoading,
    setSearching,
} = availabilitySlice.actions;

// Reducer
export const availabilityReducer = availabilitySlice.reducer;